import { supabase } from './supabase';

export interface WasserijItemChildEntry {
  id: string;
  name: string;
  code: string;
  soort: string;
  priceInclBtw: number;
  status: string;
}

export interface WasserijItemPackage {
  id: string;
  name: string;
  code: string;
  priceInclBtw: number;
}

export interface WasserijItemPriceBreakdown {
  cleaning: number;
  package: { name: string; price: number } | null;
  extras: Array<{ id: string; name: string; price: number }>;
  total: number;
}

export interface WasserijProduct {
  id: string;
  name: string;
  code: string;
  btw: string | null;
  /** Incl. BTW voor dit kleed; null als de catalogus geen prijs kon bepalen. */
  priceInclBtw?: number | null;
}

export type StatusPhase =
  | 'opgehaald'
  | 'voorinspectie'
  | 'vriezer'
  | 'kloppen'
  | 'reinigen'
  | 'drogen'
  | 'nabehandeling'
  | 'ingepakt'
  | 'bus'
  | 'afgeleverd';

export interface WasserijItemListRow {
  id: string;
  itemNumber: string;
  productName: string;
  soort: string;
  vorm: string;
  dimensionsLabel: string;
  m2: number | null;
  orderNumber: string;
  customerName: string;
  status: string;
  voorinspectieGedaan: boolean;
  klaarVoorAflevering: boolean;
  packageLabel: string | null;
  extrasCount: number;
  totalInclBtw: number;
  createdDate: string;
  lastModifiedDate: string;
}

export interface WasserijItemDetail {
  id: string;
  itemNumber: string;
  workOrderId: string;
  workOrderNumber: string;
  accountId: string;
  /** Werk Order Fulfillment_Type__c: 'Eigen verzending' = webshop (exacte maat, geen marge) | 'Chauffeur' | null (route). */
  fulfillmentType: string | null;
  product: { id: string; name: string; code: string; isPrimary: boolean };
  soort: string;
  merk: string;
  status: string;
  vorm: string;
  lengteCm: number | null;
  breedteCm: number | null;
  diameterCm: number | null;
  aantal: number;
  areaM2: number | null;
  voorinspectie: string[];
  voorinspectieOpmerkingen: string;
  voorinspectieGedaan: boolean;
  voorinspectieRandPrijs: number | null;
  naInspectie: string[];
  klaarVoorAflevering: boolean;
  heeftOndertapijt: boolean;
  /** Override-snijplan voor ondertapijt-WI: komma-gescheiden rolbreedtes (cm), null = auto. */
  ondertapijtSnijplan: string | null;
  /** Vrije notitie voor de knipper bij een ondertapijt-WI, null = geen. */
  ondertapijtSnijplanNotitie: string | null;
  photosUrl: string | null;
  /** Publieke klant-timeline `/fotos/{token}`; null als het secret ontbreekt. */
  klantlinkUrl: string | null;
  wasrapport: string;
  priceInclBtw: number;
  createdDate: string;
  lastModifiedDate: string;
  parentId: string | null;
  mutatieBron: string | null;
  package: WasserijItemPackage | null;
  extras: WasserijItemChildEntry[];
  priceBreakdown: WasserijItemPriceBreakdown;
  picklists: {
    status: string[];
    vorm: string[];
    naInspectie: string[];
    soort: string[];
    merk: string[];
  };
}

export interface WasserijItemPatch {
  Status__c?: string;
  Voorinspectie__c?: string[];
  Voorinspectie_Opmerkingen__c?: string;
  Voorinspectie_gedaan__c?: boolean;
  Soort__c?: string;
  Merk__c?: string;
  Aantal__c?: number;
  Klaar_voor_aflevering__c?: boolean;
  Vorm__c?: string;
  Werk_Order__c?: string;
  /** Override-snijplan: komma-gescheiden rolbreedtes (cm), leeg/null = auto. */
  Ondertapijt_Snijplan__c?: string | null;
  /** Vrije notitie voor de knipper, leeg/null = geen. */
  Ondertapijt_Snijplan_Notitie__c?: string | null;
}

export interface WasserijItemUpdateResult {
  nieuweLastModifiedDate: string | null;
  updatedFields: string[];
}

export interface WasserijItemWasrapportResult {
  nieuweInhoud: string;
  nieuweLastModifiedDate: string | null;
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

export async function fetchWasserijItem(id: string): Promise<WasserijItemDetail> {
  const headers = await authHeaders();
  const res = await fetch(`/api/orders/wasserij-item-detail?id=${encodeURIComponent(id)}`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    const e = new Error(err.error || 'Kon item niet laden') as Error & { status?: number };
    e.status = res.status;
    throw e;
  }
  return res.json();
}

export async function updateWasserijItem(
  id: string,
  patch: WasserijItemPatch
): Promise<WasserijItemUpdateResult> {
  const headers = await authHeaders();
  const res = await fetch('/api/orders/wasserij-item-update', {
    method: 'POST',
    headers,
    body: JSON.stringify({ itemId: id, patch }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Opslaan mislukt' }));
    const e = new Error(err.error || 'Opslaan mislukt') as Error & { status?: number; code?: string };
    e.status = res.status;
    e.code = err.code;
    throw e;
  }
  const payload = await res.json();
  return {
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    updatedFields: Array.isArray(payload.updatedFields) ? payload.updatedFields : [],
  };
}

export async function appendWasrapport(
  id: string,
  entry: string,
  context = 'Office'
): Promise<WasserijItemWasrapportResult> {
  const headers = await authHeaders();
  const res = await fetch('/api/orders/wasserij-item-wasrapport', {
    method: 'POST',
    headers,
    body: JSON.stringify({ itemId: id, entry, context }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Toevoegen mislukt' }));
    const e = new Error(err.error || 'Toevoegen mislukt') as Error & { status?: number; code?: string };
    e.status = res.status;
    e.code = err.code;
    throw e;
  }
  const payload = await res.json();
  return {
    nieuweInhoud: payload.nieuweInhoud ?? '',
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
  };
}

// ─── /items overview & search ─────────────────────────────────────────────

async function getJson<T>(
  url: string,
  errorMsg: string,
  signal?: AbortSignal
): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(url, { headers, signal });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: errorMsg }));
    const e = new Error(err.error || errorMsg) as Error & { status?: number };
    e.status = res.status;
    throw e;
  }
  return res.json();
}

async function postJson<TReq, TRes>(
  url: string,
  body: TReq,
  errorMsg: string
): Promise<TRes> {
  const headers = await authHeaders();
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: errorMsg }));
    const e = new Error(err.error || errorMsg) as Error & { status?: number; code?: string };
    e.status = res.status;
    e.code = err.code;
    throw e;
  }
  return res.json();
}

export async function fetchItemList(opts: {
  days?: number;
  statusPhase?: StatusPhase;
} = {}): Promise<WasserijItemListRow[]> {
  const params = new URLSearchParams();
  if (opts.days) params.set('days', String(opts.days));
  if (opts.statusPhase) params.set('statusPhase', opts.statusPhase);
  const qs = params.toString();
  const url = `/api/orders/items-list${qs ? `?${qs}` : ''}`;
  const payload = await getJson<{ items: WasserijItemListRow[] }>(url, 'Kon items niet laden');
  return payload.items ?? [];
}

export async function searchItems(
  query: string,
  signal?: AbortSignal
): Promise<WasserijItemListRow[]> {
  const url = `/api/orders/items-search?q=${encodeURIComponent(query)}`;
  const payload = await getJson<{ items: WasserijItemListRow[] }>(url, 'Zoeken mislukt', signal);
  return payload.items ?? [];
}

// ─── product picker (pakket / extra) ──────────────────────────────────────

export async function fetchWasserijProducts(
  kind: 'package' | 'extra',
  parentProductId: string,
  opts?: { itemId?: string }
): Promise<WasserijProduct[]> {
  const params = new URLSearchParams({ kind, parentProductId });
  if (opts?.itemId) params.set('itemId', opts.itemId);
  const url = `/api/orders/wasserij-products?${params.toString()}`;
  const payload = await getJson<{ products: WasserijProduct[] }>(url, 'Kon producten niet laden');
  return payload.products ?? [];
}

// ─── pakket / extras mutaties ─────────────────────────────────────────────

export interface PackageMutationResult {
  newPackageItemId: string | null;
  removedPackageItemId: string | null;
  newTotalInclBtw: number | null;
}

export async function setItemPackage(
  itemId: string,
  packageProductId: string | null
): Promise<PackageMutationResult> {
  const payload = await postJson<
    { itemId: string; packageProductId: string | null },
    {
      ok: boolean;
      newPackageItemId: string | null;
      removedPackageItemId: string | null;
      newTotalInclBtw: number | null;
    }
  >('/api/orders/wasserij-item-package', { itemId, packageProductId }, 'Pakket-mutatie mislukt');
  return {
    newPackageItemId: payload.newPackageItemId ?? null,
    removedPackageItemId: payload.removedPackageItemId ?? null,
    newTotalInclBtw: payload.newTotalInclBtw ?? null,
  };
}

export async function addItemExtra(
  parentItemId: string,
  productId: string,
  quantity?: number
): Promise<{ childItemId: string | null; newTotalInclBtw: number | null }> {
  const payload = await postJson<
    { parentItemId: string; productId: string; quantity?: number },
    { ok: boolean; childItemId: string | null; newTotalInclBtw: number | null }
  >(
    '/api/orders/wasserij-item-extra-add',
    { parentItemId, productId, quantity },
    'Extra toevoegen mislukt'
  );
  return {
    childItemId: payload.childItemId ?? null,
    newTotalInclBtw: payload.newTotalInclBtw ?? null,
  };
}

export async function removeItemExtra(
  childItemId: string
): Promise<{ newTotalInclBtw: number | null }> {
  const payload = await postJson<
    { childItemId: string },
    { ok: boolean; newTotalInclBtw: number | null }
  >('/api/orders/wasserij-item-extra-remove', { childItemId }, 'Extra verwijderen mislukt');
  return { newTotalInclBtw: payload.newTotalInclBtw ?? null };
}
